import { useState, useCallback } from "react";
import Modal from "components/ui/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  SortLabel,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "store";
import { LEVELS } from "utils/permissions";

export function StatusIcon({ value, checkValue = 1 }) {
  return value === checkValue ? (
    <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
  ) : (
    <XMarkIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
  );
}

export function EditAction({ onClick, title = "Edit", variant = "primary", module }) {
  const hasAccess = useAuthStore((state) => state.hasAccess);
  if (module && !hasAccess(module, LEVELS.WRITE)) return null;
  return (
    <ActionButton variant={variant} onClick={onClick} title={title}>
      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
    </ActionButton>
  );
}

export function DeleteAction({ onClick, title = "Delete", module }) {
  const hasAccess = useAuthStore((state) => state.hasAccess);
  if (module && !hasAccess(module, LEVELS.DELETE)) return null;
  return (
    <ActionButton
      variant="danger"
      onClick={onClick}
      title={title}
      className="mr-0"
    >
      <TrashIcon className="h-4 w-4" aria-hidden="true" />
    </ActionButton>
  );
}

const ADD_BTN_PRIMARY =
  "block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600";

const ADD_BTN_SECONDARY =
  "block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";

const ADD_BTN_OUTLINE =
  "inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-center text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50";

const CLEAR_BTN =
  "rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300";

const CLEAR_SORT_BTN =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300";

export const BTN_VARIANTS = {
  primary: ADD_BTN_PRIMARY,
  secondary: ADD_BTN_SECONDARY,
  outline: ADD_BTN_OUTLINE,
};

function getSortAria(direction) {
  if (direction === "asc") return "ascending";
  if (direction === "desc") return "descending";
  return "none";
}

export default function MasterPage({
  title,
  description,
  headerActions,
  filters,
  columns,
  data,
  isLoading,
  rowKey,
  renderRow,
  renderActions,
  deleteComponent: DeleteComponent,
  extraModals,
  pagination,
  sort,
}) {
  const [deleteItemId, setDeleteItemId] = useState(null);

  const colSpan = columns.length;

  const handlePageChange = useCallback(
    (page) => {
      pagination?.onPageChange?.(page);
    },
    [pagination]
  );

  const resetToFirstPage = useCallback(() => {
    if (pagination?.currentPage !== 1) {
      pagination?.onPageChange?.(1);
    }
  }, [pagination]);

  const handleToggleSort = useCallback(
    (key) => {
      sort?.toggleSort?.(key);
      resetToFirstPage();
    },
    [sort, resetToFirstPage]
  );

  const handleClearSort = useCallback(() => {
    sort?.clearSort?.();
    resetToFirstPage();
  }, [sort, resetToFirstPage]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {DeleteComponent && deleteItemId && (
        <Modal
          open={!!deleteItemId}
          setOpen={(val) => !val && setDeleteItemId(null)}
        >
          <DeleteComponent
            id={deleteItemId}
            onClose={() => setDeleteItemId(null)}
          />
        </Modal>
      )}

      {extraModals}

      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-sm text-gray-700">{description}</p>
          )}
        </div>
        {(headerActions || sort?.hasSort) && (
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex gap-3">
            {sort?.hasSort && (
              <ClearSortButton onClick={handleClearSort} />
            )}
            {headerActions}
          </div>
        )}
      </div>

      {filters && <div className="space-y-2 mb-3">{filters}</div>}

      <Table>
        <THead>
          <TR>
            {columns.map((col, i) => {
              const sortKey = col.sortKey || col.key;
              const sortEntry = sort?.sorts?.find((item) => item.key === sortKey);
              const canSort = Boolean(sort && col.sortable && !col.isActions);

              return (
                <TH
                  key={col.key || i}
                  first={i === 0}
                  last={i === columns.length - 1}
                  aria-sort={canSort ? getSortAria(sortEntry?.direction) : undefined}
                >
                  {i === columns.length - 1 && col.isActions ? (
                    <span className="sr-only">Actions</span>
                  ) : canSort ? (
                    <SortLabel
                      direction={sortEntry?.direction || null}
                      sortIndex={
                        sortEntry && sort.sorts.length > 1
                          ? sort.sorts.indexOf(sortEntry) + 1
                          : null
                      }
                      onClick={() => handleToggleSort(sortKey)}
                    >
                      {col.header}
                    </SortLabel>
                  ) : (
                    col.header
                  )}
                </TH>
              );
            })}
          </TR>
        </THead>
        <TBody>
          {isLoading ? (
            <LoadingRow colSpan={colSpan} />
          ) : !data || data.length === 0 ? (
            <EmptyRow colSpan={colSpan} message="No records found." />
          ) : (
            data.map((item) => (
              <TR key={item[rowKey]}>
                {columns.map((col, i) => (
                  <TD
                    key={col.key || i}
                    first={i === 0}
                    last={i === columns.length - 1}
                  >
                    {col.isActions
                      ? renderActions
                        ? renderActions(item, setDeleteItemId)
                        : null
                      : col.render
                        ? col.render(item)
                        : renderRow
                          ? renderRow(item, col)
                          : item[col.key]}
                  </TD>
                ))}
              </TR>
            ))
          )}
        </TBody>
      </Table>

      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          showPageJump={true}
        />
      )}
    </div>
  );
}

export function FilterRow({ children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
      {children}
    </div>
  );
}

export function FilterCell({ children, clearButton = false }) {
  return (
    <div className={clearButton ? "flex items-end" : ""}>
      {children}
    </div>
  );
}

export function ClearFiltersButton({ onClick }) {
  return (
    <FilterCell clearButton>
      <button type="button" onClick={onClick} className={CLEAR_BTN}>
        Clear Filters
      </button>
    </FilterCell>
  );
}

export function ClearSortButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} className={CLEAR_SORT_BTN}>
      Clear Sort
    </button>
  );
}

export function AddButton({
  children,
  onClick,
  variant = "primary",
  className = "",
  module,
  ...rest
}) {
  const hasAccess = useAuthStore((state) => state.hasAccess);
  if (module && !hasAccess(module, LEVELS.WRITE)) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${BTN_VARIANTS[variant] || BTN_VARIANTS.primary} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
